import { createContext, useContext, useState, useCallback, useMemo, ReactNode } from 'react';
import { PresetId } from '../theme/presets';
import { AestheticId } from '../theme/aesthetics';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------
export type AnimationLevel = 'full' | 'reduced' | 'none';

interface AppSettings {
	themePreset: PresetId;
	aesthetic: AestheticId;
	animationLevel: AnimationLevel;
}

interface AppSettingsContextValue extends AppSettings {
	setThemePreset: (id: PresetId) => void;
	setAesthetic: (id: AestheticId) => void;
	setAnimationLevel: (level: AnimationLevel) => void;
	/** false when animationLevel is 'none' */
	shouldAnimate: boolean;
	/** 1 for full, 0.5 for reduced, 0 for none */
	durationMultiplier: number;
}

// ---------------------------------------------------------------------------
// Persistence
// ---------------------------------------------------------------------------
const STORAGE_KEY = 'dashboard-settings';

const defaults: AppSettings = {
	themePreset: 'default',
	aesthetic: 'classic',
	animationLevel: 'full',
};

function readSettings(): AppSettings {
	try {
		const raw = localStorage.getItem(STORAGE_KEY);
		if (!raw) return defaults;
		const parsed = JSON.parse(raw);
		return { ...defaults, ...parsed };
	} catch {
		return defaults;
	}
}

function writeSettings(settings: AppSettings) {
	try {
		localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
	} catch {
		// localStorage unavailable — silently ignore
	}
}

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------
const AppSettingsContext = createContext<AppSettingsContextValue | null>(null);

export function AppSettingsProvider({ children }: { children: ReactNode }) {
	const [settings, setSettings] = useState<AppSettings>(readSettings);

	const setThemePreset = useCallback((id: PresetId) => {
		setSettings((prev) => {
			const next = { ...prev, themePreset: id };
			writeSettings(next);
			return next;
		});
	}, []);

	const setAesthetic = useCallback((id: AestheticId) => {
		setSettings((prev) => {
			const next = { ...prev, aesthetic: id };
			writeSettings(next);
			return next;
		});
	}, []);

	const setAnimationLevel = useCallback((level: AnimationLevel) => {
		setSettings((prev) => {
			const next = { ...prev, animationLevel: level };
			writeSettings(next);
			return next;
		});
	}, []);

	const value = useMemo<AppSettingsContextValue>(() => {
		const shouldAnimate = settings.animationLevel !== 'none';
		const durationMultiplier = settings.animationLevel === 'full' ? 1
			: settings.animationLevel === 'reduced' ? 0.5
			: 0;
		return {
			...settings,
			setThemePreset,
			setAesthetic,
			setAnimationLevel,
			shouldAnimate,
			durationMultiplier,
		};
	}, [settings, setThemePreset, setAesthetic, setAnimationLevel]);

	return (
		<AppSettingsContext.Provider value={value}>
			{children}
		</AppSettingsContext.Provider>
	);
}

export function useAppSettings(): AppSettingsContextValue {
	const ctx = useContext(AppSettingsContext);
	if (!ctx) throw new Error('useAppSettings must be used within AppSettingsProvider');
	return ctx;
}
